<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.totalSales') }}</h5>
      </div>

      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block rounded-md border border-gray-100 py-[5px] md:py-[6.5px] px-[12px] md:px-[19px] transition-all hover:bg-gray-50 dark:border-[#172036] dark:hover:bg-[#0a0e19]': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              <template v-if="selectedTimeFrame === 'monthly'">{{ directT('dashboard.monthly') }}</template>
              <template v-else-if="selectedTimeFrame === 'weekly'">{{ directT('dashboard.weekly') }}</template>
              <template v-else-if="selectedTimeFrame === 'yearly'">{{ directT('dashboard.yearly') }}</template>
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>
          <ul
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[5] dark:bg-dark dark:shadow-none"
          >
            <li>
              <button
                type="button"
                @click="changeTimeFrame('weekly')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.weekly') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="changeTimeFrame('monthly')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.monthly') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                @click="changeTimeFrame('yearly')"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
              >
                {{ directT('dashboard.yearly') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div class="trezo-card-content">
      <div v-if="isLoading" class="py-8 text-center">
        <div class="animate-spin inline-block w-8 h-8 border-[3px] border-current border-t-transparent text-primary-500 rounded-full" role="status" aria-label="loading">
          <span class="sr-only">{{ directT('common.loading') || 'Loading...' }}</span>
        </div>
      </div>
      <div v-else class="-mb-[15px] ltr:-ml-[10px] rtl:-mr-[10px] -mt-[5px]">
        <apexchart
          v-if="isClient"
          type="area"
          height="365"
          :options="totalSales"
          :series="sales"
        ></apexchart>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { getTotalSalesData } from "@/services/orderService";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

export default defineComponent({
  name: "TotalSalesChart",
  setup() {
    const { locale } = useI18n();
    const isClient = ref(false);
    const isLoading = ref(false);
    const selectedTimeFrame = ref("monthly");
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");

    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    const sales = ref([
      {
        name: "Current Sale",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
      {
        name: "Last Year Sale",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      },
    ]);

    // Chart options configuration
    const categories = computed(() => {
      if (selectedTimeFrame.value === 'weekly') {
        return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      } else if (selectedTimeFrame.value === 'yearly') {
        return ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
      } else if (selectedTimeFrame.value === 'daily') {
        return ["Morning", "Noon", "Afternoon", "Evening", "Night"];
      } else {
        // Monthly - default
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        
        // Get days in current month
        const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
        
        // If month has more than 12 days, show 12 groups instead of all days
        if (daysInMonth > 12) {
          // Create 12 labels like "Days 1-3", "Days 4-6", etc.
          const groupSize = Math.ceil(daysInMonth / 12);
          const labels = [];
          
          for (let i = 0; i < 12; i++) {
            const startDay = i * groupSize + 1;
            const endDay = Math.min(startDay + groupSize - 1, daysInMonth);
            
            if (startDay === endDay) {
              labels.push(`Day ${startDay}`);
            } else {
              labels.push(`Days ${startDay}-${endDay}`);
            }
          }
          
          return labels;
        } else {
          // Show each day
          return Array.from({ length: daysInMonth }, (_, i) => `Day ${i + 1}`);
        }
      }
    });

    const totalSales = computed(() => ({
      chart: {
        type: "area",
        height: 366,
        zoom: {
          enabled: false,
        },
      },
      colors: ["#605DFF", "#DDE4FF"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: [2, 2, 0],
        dashArray: [0, 6, 0],
      },
      grid: {
        borderColor: "#ffffff",
      },
      fill: {
        type: "gradient",
        gradient: {
          stops: [0, 90, 100],
          shadeIntensity: 1,
          opacityFrom: 0,
          opacityTo: 0.5,
        },
      },
      xaxis: {
        categories: categories.value,
        axisTicks: {
          show: false,
          color: "#B1BBC8",
        },
        axisBorder: {
          show: false,
          color: "#B1BBC8",
        },
        labels: {
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      yaxis: {
        tickAmount: 5,
        // Set max based on the highest value in the data with some padding
        max: Math.max(...sales.value[0].data, ...sales.value[1].data) * 1.2 || 100,
        min: 0,
        labels: {
          formatter: (val: number) => {
            return "$" + val + "K";
          },
          style: {
            colors: "#8695AA",
            fontSize: "12px",
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        fontSize: "12px",
        horizontalAlign: "left",
        itemMargin: {
          horizontal: 8,
          vertical: 0,
        },
        labels: {
          colors: "#64748B",
        },
        markers: {
          width: 9,
          height: 9,
          offsetX: -2,
          offsetY: -0.5,
          radius: 2,
          shape: "diamond",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return "$" + val + "k";
          },
        },
      },
    }));

    // Fetch sales data
    const fetchSalesData = async () => {
      console.log(`[TotalSalesChart] Fetching sales data for period: ${selectedTimeFrame.value}`);
      isLoading.value = true;
      
      try {
        const { currentYear, previousYear, error } = await getTotalSalesData(null, selectedTimeFrame.value);
        
        if (error) {
          console.error('[TotalSalesChart] Error fetching sales data:', error);
        } else {
          console.log('[TotalSalesChart] Sales data received:', { currentYear, previousYear });
          
          // Update series data
          sales.value = [
            {
              name: "Current Sale",
              data: currentYear,
            },
            {
              name: "Last Year Sale",
              data: previousYear,
            },
          ];
        }
      } catch (err) {
        console.error('[TotalSalesChart] Exception in fetchSalesData:', err);
      } finally {
        isLoading.value = false;
      }
    };

    // Function to change time frame
    const changeTimeFrame = (period: string) => {
      console.log(`[TotalSalesChart] Changing period to: ${period}`);
      selectedTimeFrame.value = period;
      toggleActive();
      fetchSalesData();
    };

    // Watch for changes in time frame
    watch(selectedTimeFrame, () => {
      fetchSalesData();
    });

    onMounted(() => {
      isClient.value = true;
      // Fetch initial data
      fetchSalesData();
    });

    return {
      isClient,
      isLoading,
      sales,
      totalSales,
      selectedTimeFrame,
      changeTimeFrame,
      isActive,
      toggleActive,
      directT,
    };
  },
});
</script>
